import React, { useEffect, useState, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Alert,
    Snackbar,
    IconButton,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import config from '../config';
import DataGrid, { Column, Editing, Paging, Selection } from 'devextreme-react/data-grid';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale, loadMessages } from 'devextreme/localization';
import * as XLSX from 'xlsx';
import 'devextreme/dist/css/dx.light.compact.css';
import ProgressTracker from './ProgressTracker';

const OULinks = () => {
    locale(sessionStorage.getItem('locale') || 'ru');
    loadMessages(ruMessages);

    const [links, setLinks] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [userId, setUserId] = useState(Date.now().toString());
    const dataGridRef = useRef(null);

    useEffect(() => {
        fetchLinks();
    }, []);

    const fetchLinks = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.apiBaseUrl}/admin/ou-links`, {
                headers: { 'x-access-token': token },
            });
            setLinks(response.data);
        } catch (err) {
            setError('Ошибка при загрузке данных');
        }
    };

    const handleRowInserting = async (e) => {
        try {
            const token = localStorage.getItem('token');
            const tempId = Date.now();
            const newRecord = { ...e.data, id: tempId };

            setLinks([...links, newRecord]);

            await axios.post(`${config.apiBaseUrl}/admin/ou-links`, e.data, {
                headers: { 'x-access-token': token },
            });

            setSuccess('Запись успешно добавлена');
        } catch (err) {
            setError('Ошибка при добавлении записи: ' + err.message);
            e.cancel = true;
        }
    };

    const handleRowUpdating = async (e) => {
        try {
            const token = localStorage.getItem('token');
            const currentRow = links.find(link => link.id === e.key);
            const updatedData = { ...currentRow, ...e.newData };
            await axios.put(`${config.apiBaseUrl}/admin/ou-links/${e.key}`, updatedData, {
                headers: { 'x-access-token': token },
            });
            setSuccess('Запись успешно обновлена');
            setLinks(links.map(link => link.id === e.key ? updatedData : link));
        } catch (err) {
            setError('Ошибка при обновлении записи: ' + err.message);
            e.cancel = true;
        }
    };

    const handleRowRemoving = async (e) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${config.apiBaseUrl}/admin/ou-links/${e.key}`, {
                headers: { 'x-access-token': token },
            });
            setSuccess('Запись успешно удалена');
            setLinks(links.filter(link => link.id !== e.key));
        } catch (err) {
            setError('Ошибка при удалении записи: ' + err.message);
            e.cancel = true;
        }
    };

    const handleBulkDelete = async () => {
        const selectedRows = dataGridRef.current.instance.getSelectedRowsData();
        if (selectedRows.length === 0) {
            setError('Выберите строки для удаления');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const idsToDelete = selectedRows.map(row => row.id);

            await axios.post(`${config.apiBaseUrl}/admin/bulk-delete`, { ids: idsToDelete }, {
                headers: { 'x-access-token': token },
            });

            setSuccess('Выбранные записи успешно удалены');
            fetchLinks();
        } catch (err) {
            setError('Ошибка при удалении записей: ' + err.message);
        }
    };

    const handleCloseError = () => {
        setError('');
    };

    const handleCloseSuccess = () => {
        setSuccess('');
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const formattedData = jsonData.slice(1).map((row, index) => ({
                id: Date.now() + index,
                number: row[0],
                type: row[1],
                link: row[2],
                comments: row[3],
            }));

            uploadData(formattedData);
        };
        reader.readAsArrayBuffer(file);
    };

    const uploadData = async (data) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${config.apiBaseUrl}/admin/ou-import`, data, {
                headers: { 'x-access-token': token },
            });
            setSuccess('Данные успешно импортированы');
            fetchLinks();
        } catch (err) {
            setError('Ошибка при импорте данных: ' + err.message);
        }
    };

    const handleExportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(links);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Ссылки');
        XLSX.writeFile(workbook, 'Ссылки.xlsx');
    };

    return (
        <Container maxWidth="lg">
            <Box mt={5} textAlign="center">
                <Typography variant="h4" gutterBottom>
                    Ссылки на сайты образовательных учреждений
                </Typography>

                <Box mb={3}>
                    <input
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        id="upload-file"
                        type="file"
                        onChange={handleFileUpload}
                    />
                    <label htmlFor="upload-file">
                        <Button variant="contained" component="span">
                            Импорт из Excel
                        </Button>
                    </label>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleBulkDelete}
                        style={{ marginLeft: '10px' }}
                    >
                        Удалить выбранные
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExportToExcel}
                        style={{ marginLeft: '10px' }}
                    >
                        Экспорт в Excel
                    </Button>
                </Box>



                <DataGrid
                    ref={dataGridRef}
                    dataSource={links}
                    keyExpr="id"
                    showBorders={true}
                    onRowInserting={handleRowInserting}
                    onRowUpdating={handleRowUpdating}
                    onRowRemoving={handleRowRemoving}
                >
                    <Selection mode="multiple" />
                    <Paging enabled={true} defaultPageSize={20} />
                    <Editing
                        mode="row"
                        allowUpdating={true}
                        allowDeleting={true}
                        allowAdding={true}
                        useIcons={true}
                    />
                    <Column dataField="number" caption="Номер ОУ" dataType="number" width="80" />
                    <Column dataField="type" caption="Тип" />
                    <Column
                        dataField="link"
                        caption="Ссылка"
                        cellRender={(data) => (
                            <a href={data.value} target="_blank" rel="noopener noreferrer">
                                {data.value}
                            </a>
                        )}
                    />
                    <Column dataField="comments" caption="Комментарии" />
                </DataGrid>
            </Box>

            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="error"
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={handleCloseError}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!success}
                autoHideDuration={5000}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="success"
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={handleCloseSuccess}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    {success}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default OULinks;